import React, { FC, useState, useEffect } from "react";
import { Form, Checkbox, Table, Button, Switch, Collapse } from "antd";
import type { ColumnType } from "antd/lib/table";
import { Settings, FeeEarnerLevelWithGpmVisibility } from "../../data-types";
import styles from "./gpm-visibility.css";
import { useAuth } from "../../../hooks";
import { GpmSlider } from "../gpm-slider";
import { useTaxonomyLabel, VPDLabel } from "~/context/taxonomy-context";

const { Panel } = Collapse;

const {
  ["container"]: containerClassName,
  ["table-container"]: tableContainerClassName,
  ["submit-button"]: submitButtonClassName,
} = styles as any;

export type GpmVisibilityProps = {
  settings: Partial<Settings>;
  feeEarnerLevels: FeeEarnerLevelWithGpmVisibility[] | undefined;
  onUpdateSettings: (settings: Settings) => Promise<void>;
  onUpdateFeeEarnerLevels: (
    data: {
      id: number;
      restrictedGpmVisible: boolean;
    }[]
  ) => void;
  onSubmit: () => void;
};

export const GpmVisibility: FC<GpmVisibilityProps> = ({
  settings,
  feeEarnerLevels,
  onUpdateSettings,
  onUpdateFeeEarnerLevels,
  onSubmit,
}) => {
  const { restrictGpmVisibility, showGpmSlider } = settings;
  const { currentUser } = useAuth();
  const [showGpmSliderState, setShowGpmSliderState] = useState<boolean>(showGpmSlider ?? false);
  const [checkedItems, setCheckedItems] = useState<{ [id: number]: boolean }>({});

  const taxonomy = useTaxonomyLabel();

  useEffect(() => {
    if (feeEarnerLevels) {
      const initialChecks = feeEarnerLevels.reduce((acc, level) => {
        acc[level.id] = level.restrictedGpmVisible;
        return acc;
      }, {} as { [id: number]: boolean });
      setCheckedItems(initialChecks);
    }
  }, [feeEarnerLevels]);

  const handleRestrictGpmVisibilityChange = () => {
    // const currentRestrictValue = form.getFieldValue("restrictGpmVisibility");

    // if (currentRestrictValue) {
    //   setShowGpmSliderState(false);
    //   form.setFieldsValue({ showGpmSlider: false });
    // }

    form.submit();
  };

  const handleShowGpmSliderChange = (checked: boolean) => {
    setShowGpmSliderState(checked);
    form.setFieldsValue({ showGpmSlider: checked });
    form.submit();
  };

  const handleCheckboxChange = (record: FeeEarnerLevelWithGpmVisibility) => {
    const newValue = !checkedItems[record.id];
    setCheckedItems({
      ...checkedItems,
      [record.id]: newValue,
    });
    onUpdateFeeEarnerLevels([
      {
        id: record.id,
        restrictedGpmVisible: newValue,
      },
    ]);
  };

  const tableColumns: ColumnType<FeeEarnerLevelWithGpmVisibility>[] = [
    {
      title: VPDLabel("FeeEarnerLevel"),
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Show GPM?",
      dataIndex: "restrictedGpmVisible",
      key: "restrictedGpmVisible",
      render: (value: boolean, record: FeeEarnerLevelWithGpmVisibility) => (
        <Checkbox checked={checkedItems[record.id]} onChange={() => handleCheckboxChange(record)} />
      ),
    },
  ];

  const initialValues = { restrictGpmVisibility, showGpmSlider };

  const [form] = Form.useForm();

  const doOnFinish = async (values: any) => {
    await onUpdateSettings(values);
  };

  return (
    <div className={containerClassName}>
      <Form form={form} initialValues={initialValues} onFinish={doOnFinish}>
        <Form.Item
          name="restrictGpmVisibility"
          label="Restrict GPM % visibility to certain users"
          valuePropName="checked"
        >
          <Checkbox onChange={handleRestrictGpmVisibilityChange} />
        </Form.Item>
        <Form.Item name="showGpmSlider" valuePropName="checked" label="GPM slider">
          <div style={{ display: "flex", alignItems: "center" }}>
            <span>{restrictGpmVisibility ? "Show for users who cannot see GPM %" : "Hide"}</span>
            <Switch
              checked={showGpmSliderState}
              onChange={handleShowGpmSliderChange}
              style={{ margin: "0 10px" }}
            />
            <span>{restrictGpmVisibility ? "Show for everyone" : "Show"}</span>
          </div>
        </Form.Item>
      </Form>
      <Collapse style={{ maxWidth: "800px", marginBottom: "20px" }}>
        <Panel header="More info" key="1">
          <p>
            Below is an example GPM slider. The range of GPM values contained within the red, amber,
            and green regions is controlled by the &quot;GPM Required&quot; and &quot;GPM Warning
            threshold&quot; in the &quot;Organisation Settings&quot; tab. A GPM below the required
            GPM will appear in the red, while the size of the amber region is taken to be a
            percentage of the required GPM. For instance, if the required GPM is 45%, and the
            warning threshold is 10%, then GPMs between 45% and 49.5% will be in the amber region.
          </p>
          <GpmSlider
            gpm={75}
            gpmThreshold={{ gpmDangerThreshold: 60, gpmWarningThreshold: 10, source: "none" }}
            showGpm={restrictGpmVisibility ?? false}
            isFeeEarnerLevelGpm={false}
          />
        </Panel>
      </Collapse>
      {restrictGpmVisibility && (
        <>
          <div>
            <h2>{taxonomy.FeeEarnerLevel}</h2>
            <p>Any {taxonomy.FeeEarner}s in the selected groups below will see GPM % in VPD:</p>
          </div>
          <div className={tableContainerClassName}>
            <Table
              dataSource={feeEarnerLevels}
              scroll={{ x: "max-content" }}
              columns={tableColumns}
              rowKey={(record) => record.id}
              pagination={false}
            />
          </div>
          {currentUser?.isAdUser && (
            <div>
              <h2>Active Directory Group</h2>
              <p>
                As well as {taxonomy.FeeEarner}s in the selected groups above, any user in the
                Active Directory Group VPD_Enable_GPM will be able to see the GPM in VPD. Active
                Directory groups permissions are controlled by your IT department.
              </p>
            </div>
          )}
          <div className={submitButtonClassName}>
            <Button onClick={() => onSubmit()} type="primary">
              Update GPM Visibility
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
